import { HttpErrorResponse } from '@angular/common/http';
import { ErrorEvent as KendoErrorEvent } from '@progress/kendo-angular-upload';

interface KendoUploadErrorResponse {
    status: number;
    statusText: string;
    error?: unknown;
}

type KendoErrorEventWithResponse = KendoErrorEvent & {
    response?: KendoUploadErrorResponse;
};

export interface ApiErrorResponse {
    title?: string;
    status?: number;
    detail?: string;
    traceId?: string;
    [key: string]: any;
}

export interface ErrorMessageOptions {
    defaultMessage?: string;
    includeStatus?: boolean;
    includeTitle?: boolean;
    statusMessages?: { [statusCode: number]: string };
}

function isApiErrorResponse(body: unknown): body is ApiErrorResponse {
    return body !== null && typeof body === 'object' && ('detail' in body || 'title' in body);
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
}

export const ERROR_SNACKBAR_DURATION = 8000;

function getDefaultStatusMessage(status: number, fallbackMessage: string): string {
    switch (status) {
        case 0:
            return 'Unable to connect to the server. Please check your internet connection and try again.';
        case 400:
            return 'Bad Request: Please check your input and try again.';
        case 401:
            return 'Unauthorized: Please log in and try again.';
        case 403:
            return 'Forbidden: You do not have permission to perform this action.';
        case 404:
            return 'Not Found: The requested resource could not be found.';
        case 409:
            return 'Conflict: The request could not be completed due to a conflict.';
        case 422:
            return 'Validation Error: Please check your input and try again.';
        case 429:
            return 'Too Many Requests: Please wait a moment before trying again.';
        case 500:
            return 'Internal Server Error: Something went wrong on our end.';
        case 502:
            return 'Bad Gateway: Service temporarily unavailable.';
        case 503:
            return 'Service Unavailable: Please try again later.';
        default:
            return fallbackMessage;
    }
}

export function extractErrorMessage(error: HttpErrorResponse | any, options: ErrorMessageOptions = {}): string {
    const {
        defaultMessage = 'An unexpected error occurred. Please try again.',
        includeStatus = false,
        includeTitle = false,
        statusMessages = {}
    } = options;

    if (error instanceof HttpErrorResponse) {
        const errorBody = error.error;

        if (statusMessages[error.status]) {
            return statusMessages[error.status];
        }

        if (Array.isArray(errorBody)) {
            const validMessages = errorBody.filter(msg => typeof msg === 'string' && msg.trim().length > 0);
            return validMessages.length > 0 ? validMessages.join('; ') : defaultMessage;
        }

        if (isApiErrorResponse(errorBody)) {
            const apiError = errorBody;

            if (apiError.detail) {
                let message = apiError.detail;

                if (includeTitle && apiError.title) {
                    message = `${apiError.title}: ${message}`;
                }

                if (includeStatus && error.status) {
                    message = `[${error.status}] ${message}`;
                }

                return message;
            }

            if (apiError.title) {
                return apiError.title;
            }
        }

        return getDefaultStatusMessage(error.status, error.message || defaultMessage);
    }

    if (Array.isArray(error)) {
        const validMessages = error.filter(msg => typeof msg === 'string' && msg.trim().length > 0);
        return validMessages.length > 0 ? validMessages.join('; ') : defaultMessage;
    }

    if (error instanceof Error) {
        return error.message || defaultMessage;
    }

    if (typeof error === 'string') {
        return error;
    }

    if (error && typeof error === 'object') {
        if (error.error && Array.isArray(error.error)) {
            const validMessages = error.error.filter(msg => typeof msg === 'string' && msg.trim().length > 0);
            return validMessages.length > 0 ? validMessages.join('; ') : defaultMessage;
        }

        if (isApiErrorResponse(error.error)) {
            const errorBody = error.error;
            if (errorBody.detail) return errorBody.detail;
            if (errorBody.title) return errorBody.title;
        }

        if (error.detail) return error.detail;
        if (error.message) return error.message;
        if (typeof error.status === 'number') {
            return getDefaultStatusMessage(error.status, error.message || defaultMessage);
        }
    }

    return defaultMessage;
}

export function getUserFacingErrorMessage(
    error: HttpErrorResponse | any,
    defaultMessage: string = 'Something went wrong. Please try again.'
): string {
    return extractErrorMessage(error, {
        defaultMessage,
        includeStatus: false,
        includeTitle: false
    });
}

export function isValidationError(error: HttpErrorResponse | any): boolean {
    if (error instanceof HttpErrorResponse) {
        return error.status === 400 || error.status === 422;
    }
    return false;
}

export function isNetworkError(error: HttpErrorResponse | any): boolean {
    if (error instanceof HttpErrorResponse) {
        return error.status === 0 || error.status >= 500;
    }
    return false;
}

export class ErrorHandler {
    static handleApiError(
        error: HttpErrorResponse | any
    ): {
        message: string;
        isValidationError: boolean;
        isNetworkError: boolean;
        statusCode?: number;
    } {
        return {
            message: extractErrorMessage(error),
            isValidationError: isValidationError(error),
            isNetworkError: isNetworkError(error),
            statusCode: error instanceof HttpErrorResponse ? error.status : undefined
        };
    }

    static formatForLogging(error: any): string {
        if (error instanceof HttpErrorResponse) {
            const errorBody = error.error as ApiErrorResponse;
            return `HTTP ${error.status}: ${error.url} - ${errorBody?.detail ||
                error.message} (TraceId: ${errorBody?.traceId || 'N/A'})`;
        }

        return error?.message || error?.toString() || 'Unknown error';
    }
}

export interface UploadErrorInfo {
    message: string;
    statusCode: number | null;
    statusText: string | null;
    responseBody: unknown;
    isNetworkError: boolean;
}

export interface UploadUserMessageOptions {
    fallbackMessage?: string;
    invalidFileMessage?: string;
}

function hasExtractedServerMessage(info: UploadErrorInfo): boolean {
    const body = info.responseBody as Record<string, unknown> | null;
    return !!(body && (typeof body === 'string' || body.detail || body.message || body.Message));
}

export function extractUploadError(event: KendoErrorEventWithResponse): UploadErrorInfo {
    const response = event.response;

    if (!response || response.status === 0) {
        return {
            message: 'Unable to connect to the server. Please check your connection and try again.',
            statusCode: 0,
            statusText: null,
            responseBody: null,
            isNetworkError: true
        };
    }

    const responseError = response.error as Record<string, unknown> | string | unknown[] | null;
    let message: string;

    if (typeof responseError === 'string') {
        message = responseError;
    } else if (responseError && typeof responseError === 'object' && !Array.isArray(responseError)) {
        const errorObj = responseError as Record<string, unknown>;
        if (errorObj.detail && typeof errorObj.detail === 'string') {
            message = errorObj.detail;
        } else if (typeof errorObj.message === 'string') {
            message = errorObj.message;
        } else if (typeof errorObj.Message === 'string') {
            message = errorObj.Message;
        } else if (errorObj.title && typeof errorObj.title === 'string') {
            message = errorObj.title;
        } else {
            message = response.statusText || `Upload failed with status ${response.status}`;
        }
    } else if (Array.isArray(responseError)) {
        const validMessages = responseError.filter(
            (msg): msg is string => typeof msg === 'string' && msg.trim().length > 0
        );
        message = validMessages.length > 0 ? validMessages.join('; ') : response.statusText;
    } else {
        message = response.statusText || `Upload failed with status ${response.status}`;
    }

    return {
        message,
        statusCode: response.status,
        statusText: response.statusText,
        responseBody: responseError,
        isNetworkError: false
    };
}

export function formatUploadErrorForLogging(event: KendoErrorEventWithResponse): string {
    const info = extractUploadError(event);

    if (info.isNetworkError) {
        return `[Upload Error] Network/CORS Error - Status: 0, Message: ${info.message}`;
    }

    let bodyStr: string;
    if (info.responseBody == null) {
        bodyStr = 'N/A';
    } else {
        try {
            bodyStr = JSON.stringify(info.responseBody, null, 2);
        } catch {
            bodyStr = '[Unserializable response body]';
        }
    }
    return `[Upload Error] HTTP ${info.statusCode} - ${info.statusText}\nMessage: ${info.message}\nBody: ${bodyStr}`;
}

export function getUploadUserMessage(
    event: KendoErrorEventWithResponse,
    optionsOrFallback: string | UploadUserMessageOptions = 'File upload failed. Please try again.'
): string {
    const options: UploadUserMessageOptions =
        typeof optionsOrFallback === 'string' ? { fallbackMessage: optionsOrFallback } : optionsOrFallback;

    const { fallbackMessage = 'File upload failed. Please try again.', invalidFileMessage } = options;

    const info = extractUploadError(event);

    if (info.isNetworkError) {
        return 'Unable to connect to the upload server. Please check your network connection and try again.';
    }

    const hasServerMessage = hasExtractedServerMessage(info);

    switch (info.statusCode) {
        case 400:
            if (hasServerMessage) return info.message;
            return invalidFileMessage || 'Invalid request. Please check the file and try again.';
        case 401:
        case 403:
            return 'You are not authorized to upload files. Please log in again.';
        case 404:
            return 'Upload service not found. Please try again later.';
        case 413:
            return 'The file is too large. Please upload a smaller file.';
        case 415:
            if (hasServerMessage) return info.message;
            return invalidFileMessage || 'Unsupported file type. Please upload a .jpg, .jpeg, .png, or .pdf file.';
        case 422:
            return info.message || 'The file could not be processed. Please check the format and try again.';
        case 500:
        case 502:
        case 503:
            return 'The server encountered an error. Please try again later.';
        default:
            return info.message || fallbackMessage;
    }
}
